import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, Save, Check } from 'lucide-react';
import { groupTemplatesByDesign, parseIdCardTemplate } from '../../utils/idCardFormats';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

function OrientationBadge({ format }) {
  const label = format === 'landscape' ? '3×2' : '2×3';
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
      {label}
    </span>
  );
}

export default function IdCardTemplateSelector({
  templates,
  activeTemplateId,
  saving,
  onSave,
  onDraftChange,
}) {
  const [draftId, setDraftId] = useState('');

  const groups = useMemo(() => groupTemplatesByDesign(templates), [templates]);

  useEffect(() => {
    setDraftId(activeTemplateId || templates?.[0]?.id || '');
  }, [activeTemplateId, templates]);

  useEffect(() => {
    const tpl = templates?.find((t) => String(t.id) === String(draftId)) || null;
    onDraftChange?.(tpl);
  }, [draftId, templates, onDraftChange]);

  const isDirty = draftId && String(draftId) !== String(activeTemplateId);

  if (!templates?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No ID card templates available. Run database migrations.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-5">
        {groups.map((group) => (
          <div key={group.design} className="space-y-2">
            <div className="flex items-center gap-2">
              <span
                className="h-4 w-4 rounded-full ring-1 ring-border shrink-0"
                style={{ backgroundColor: group.swatch }}
              />
              <span className="text-sm font-semibold text-foreground">{group.label}</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {group.templates.map((t) => {
                const { format } = parseIdCardTemplate(t);
                const selected = String(t.id) === String(draftId);
                const isActive = String(t.id) === String(activeTemplateId);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setDraftId(t.id)}
                    className={[
                      'text-left rounded-xl border p-3 transition-colors',
                      selected
                        ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-950/20 ring-1 ring-blue-500/40'
                        : 'border-border bg-background hover:border-blue-300/60 hover:bg-muted/30',
                    ].join(' ')}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{t.name}</p>
                        {t.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <OrientationBadge format={format} />
                        {isActive && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400">
                            <Check className="h-3 w-3" /> Active
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        className={PRIMARY_BTN}
        disabled={!isDirty || saving}
        onClick={() => onSave?.(draftId)}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save template
      </Button>
    </div>
  );
}
